import React, { useContext } from "react";
import { DiscountsTableProps } from "./types";
import { Link } from "react-router-dom";
import moment from "moment";
import { DiscountDescription } from "../../../components/misc/DiscountDescription";
import MaterialTable from "@material-table/core";
import { Box, Paper, Tooltip, useTheme } from "@material-ui/core";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import { CheckCircle, CheckCircleOutline } from "@mui/icons-material";
import { prepareExportData } from "../../../utils";
import { Authorized } from "../../../Authorized";
import { permissions } from "../../../constants";
import { UserContext } from "../../../UserProvider";
import { Authenticated } from "../../../models/User";
import { useI18n } from "../../../hooks/useI18n";

const DiscountsCollection = ({
  discounts,
  screenSize
}: DiscountsTableProps) => {
  const rowsPerPage = screenSize === "desktop" ? 10 : 5;
  const theme = useTheme();
  const { t } = useI18n();

  const user = useContext(UserContext) as Authenticated;

  /* Restrict to root for now */
  const userHasAccess = user.roles.some(role =>
    permissions.root.includes(role)
  );

  const exportDataFormat = [
    { type: "datetime", index: 1 },
    { type: "datetime", index: 2 }
  ];

  return (
    <MaterialTable
      title={t("common.messages.coupons")}
      columns={[
        {
          title: "Code",
          field: "code",
          render: (row: any) => (
            <Box>
              <div>
                {userHasAccess && row.active ? (
                  <Link to={`/discounts/${row.id}`}>{row.code}</Link>
                ) : (
                  <>{row.code}</>
                )}
              </div>
              <DiscountDescription discount={row} />
            </Box>
          )
        },
        {
          title: "Added",
          field: "added",
          render: (row: any) => moment(row.added).format("YYYY-MM-DD")
        },
        {
          title: t("common.formFields.validTo"),
          field: "end",
          render: (row: any) => moment(row.end).format("YYYY-MM-DD")
        },
        {
          title: "Used",
          field: "used",
          render: (row: any) => (
            <Box>
              <Tooltip
                title={`${
                  row.used
                    ? t("common.messages.used")
                    : t("common.messages.notUsed")
                }`}
              >
                {row.used ? (
                  <CheckCircle style={{ color: theme.palette.success.dark }} />
                ) : (
                  <CheckCircleOutline
                    style={{ color: theme.palette.grey["400"] }}
                  />
                )}
              </Tooltip>
            </Box>
          )
        },
        {
          title: "Active",
          field: "active",
          export: true,
          hidden: true
        }
      ]}
      data={discounts ? (discounts as []) : []}
      components={{
        Container: props => <Paper {...props} elevation={0} />
      }}
      options={{
        showTitle: false,
        paging: true,
        pageSize: rowsPerPage, // make initial page size
        emptyRowsWhenPaging: false, // To avoid of having empty rows
        search: true,
        pageSizeOptions: [10, 20, 50, 100],
        exportMenu:
          screenSize === "desktop"
            ? [
                {
                  label: t("common.export.exportPdf"),
                  exportFunc: (cols, data) => {
                    const d = prepareExportData(cols, data, exportDataFormat);
                    return ExportPdf(d[0], d[1], `Discounts`);
                  }
                },
                {
                  label: t("common.export.exportCsv"),
                  exportFunc: (cols, data) => {
                    const d = prepareExportData(cols, data, exportDataFormat);
                    return ExportCsv(d[0], d[1], `Discounts`);
                  }
                }
              ]
            : [],
        exportAllData: true
      }}
    />
  );
};

export default DiscountsCollection;
